import { BlockTitle } from "@blocks/Blocks";
import { useWindowSize } from "@hooks/useWindowSize";
import LatestNewsSection from "@pages/Global/components/LatestNewsSection";
import SmallCard from "@pages/Global/components/SmallCard";
import { getCardLayout } from "@pages/Global/utils/helpers";
import useOKTAUserStore, { OKTAUserTypeEnum } from "@store/useOKTAUserStore";
import { SanitizedSectionArticle } from "@typings/Cue";
import { sectionNavigationItems } from "@util/constant";
import { cn } from "@util/helpers";

type EconomyPolicySectionProps = {
  /** The articles to be displayed in the section */
  articles: SanitizedSectionArticle[];
  /** The latest news articles to be displayed in the section */
  latestNews: SanitizedSectionArticle[];
  /** Optional className for the section */
  className?: string;
  /** Optional link params */
  linkParams?: URLSearchParams;
};

/**
 * EconomyPolicySection component to display a list of articles related to /singapore/economy-policy and /keywords/asia-pacific.
 * It renders a section with a title, a list of articles as SmallCard components, and a LatestNewsSection.
 */
const EconomyPolicySection = ({
  articles,
  latestNews,
  linkParams,
}: EconomyPolicySectionProps): React.ReactElement => {
  const OKTAUserInfo = useOKTAUserStore((state) => state.userInfo);
  const { isScreenLG } = useWindowSize();

  const sectionData = sectionNavigationItems["singapore/economy-policy"];

  if (!articles.length && !latestNews.length) return <></>;

  return (
    <section
      data-testid="global-economy-policy-section-component"
      className={cn("mb-10 lg:grid lg:grid-cols-3 lg:gap-4")}
    >
      <div className="col-span-2 border-t border-gray-850 pt-4">
        <BlockTitle
          link={sectionData.link}
          text={sectionData.label.toUpperCase()}
        />

        <div
          className={cn(
            "relative mb-6 grid w-full divide-gray-175 lg:grid-cols-2 lg:divide-x"
          )}
        >
          {articles.map((article, index) => {
            const CardLayout = isScreenLG ? SmallCard : getCardLayout(index);

            return (
              <CardLayout
                key={index}
                article={article}
                displayBlurb={index === 0}
                displayEdited={!isScreenLG && index === 0}
                displayUpdated={!isScreenLG}
                displaySection
                contentClassName={cn("px-0", {
                  "lg:pr-3": index % 2 === 0,
                  "lg:pl-3": index % 2 !== 0,
                })}
                className={cn(
                  "relative my-4 lg:my-6",
                  "after:absolute after:-bottom-4 after:w-full after:border-b after:border-gray-175 after:lg:-bottom-6 ",
                  { "mt-4": index === 0 || index === 1 },
                  {
                    "after:hidden": index === articles.length - 1,
                  },
                  {
                    "after:lg:hidden": index === articles.length - 2,
                  },
                  { "border-none": index % 2 === 0 }
                )}
                linkParams={linkParams}
                disablePaywall={
                  OKTAUserInfo?.usertype !== OKTAUserTypeEnum.SUBSCRIBER
                }
              />
            );
          })}
        </div>
      </div>

      <LatestNewsSection articles={latestNews} className="col-span-1" />
    </section>
  );
};

export default EconomyPolicySection;
